import React, { useEffect, useMemo } from 'react';
import {
  Box,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RcTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CompanyStats } from '../../../store/responseTypes/company';
import { companiesApi } from '../../../store/services/CompaniesService';

export interface ChartWindow {
  from: string; // ISO Z
  to: string; // ISO Z
  defaultedFromLifetime: boolean;
}

interface CompanyHistoryModalProps {
  company: CompanyStats | null;
  isOpen: boolean;
  onClose: () => void;
  pagePeriod: { from: string | null; to: string | null };
}

// Chart metric names (BE series keys, all non-current-only)
const SERIES_METRICS = [
  { key: 'signedAgreements', label: 'Signed agreements' },
  { key: 'registeredAgreements', label: 'Registered agreements' },
  { key: 'projects', label: 'Projects' },
  { key: 'userSessions', label: 'Sessions' },
  { key: 'filesUploaded', label: 'Files uploaded' },
  { key: 'storageUsedBytes', label: 'Storage used' },
] as const;

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const TABULAR = { fontVariantNumeric: 'tabular-nums' as const };

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const v = bytes / Math.pow(1000, i);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
};

const formatNumber = (n: number) => n.toLocaleString('en-US');

const formatBucketLabel = (
  iso: string,
  granularity: 'day' | 'week' | 'month',
): string => {
  const d = new Date(iso);
  const day = d.getUTCDate();
  const month = MONTHS_SHORT[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  if (granularity === 'month') return `${month} ${year}`;
  return `${month} ${day}`;
};

const formatBucketRange = (
  startIso: string,
  endIso: string,
  granularity: 'day' | 'week' | 'month',
): string => {
  // endIso is half-open exclusive — show inclusive end (one ms earlier day).
  const end = new Date(endIso);
  const inclusiveEnd = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate() - 1),
  );
  if (granularity === 'day') {
    return formatBucketLabel(startIso, 'day');
  }
  return `${formatBucketLabel(startIso, granularity)} – ${formatBucketLabel(
    inclusiveEnd.toISOString(),
    granularity,
  )}`;
};

// Default window when page period is lifetime: last 12 months ending now (UTC).
const buildDefaultWindow = (now: Date = new Date()): ChartWindow => {
  const toMs = now.getTime();
  const from = new Date(
    Date.UTC(
      now.getUTCFullYear() - 1,
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
    ),
  );
  return {
    from: from.toISOString(),
    to: new Date(toMs).toISOString(),
    defaultedFromLifetime: true,
  };
};

const resolveChartWindow = (
  pagePeriod: { from: string | null; to: string | null },
): ChartWindow => {
  if (pagePeriod.from && pagePeriod.to) {
    return {
      from: pagePeriod.from,
      to: pagePeriod.to,
      defaultedFromLifetime: false,
    };
  }
  return buildDefaultWindow();
};

interface ChartPanelProps {
  metric: typeof SERIES_METRICS[number];
  values: number[] | undefined;
  buckets: { bucketStart: string; bucketEnd: string }[];
  granularity: 'day' | 'week' | 'month';
  capacityBytes?: number;
}

const ChartPanel: React.FC<ChartPanelProps> = ({
  metric,
  values,
  buckets,
  granularity,
  capacityBytes,
}) => {
  const isBytes = metric.key === 'storageUsedBytes';
  const data = useMemo(() => {
    if (!values) return [];
    return buckets.map((b, i) => ({
      label: formatBucketLabel(b.bucketStart, granularity),
      range: formatBucketRange(b.bucketStart, b.bucketEnd, granularity),
      value: values[i] ?? 0,
    }));
  }, [values, buckets, granularity]);

  if (!values) {
    return (
      <Box>
        <Text
          fontSize="xs"
          letterSpacing="0.1em"
          textTransform="uppercase"
          color="gray.500"
          fontWeight={500}
          mb={2}
        >
          {metric.label}
        </Text>
        <Text fontSize="xs" color="gray.400">
          Not available
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text
        fontSize="xs"
        letterSpacing="0.1em"
        textTransform="uppercase"
        color="gray.500"
        fontWeight={500}
        mb={2}
      >
        {metric.label}
      </Text>
      <Box h="160px">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              minTickGap={16}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              width={isBytes ? 56 : 40}
              tickFormatter={(v: number) =>
                isBytes ? formatBytes(v) : formatNumber(v)
              }
            />
            <RcTooltip
              cursor={{ stroke: '#d1d5db', strokeDasharray: '3 3' }}
              contentStyle={{
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #e5e7eb',
              }}
              formatter={(v: number) =>
                isBytes ? [formatBytes(v), metric.label] : [formatNumber(v), metric.label]
              }
              labelFormatter={(_label, payload) => {
                const p = Array.isArray(payload) ? payload[0] : undefined;
                const range = p && p.payload ? p.payload.range : '';
                return range || '';
              }}
            />
            {isBytes && capacityBytes && capacityBytes > 0 && (
              <ReferenceLine
                y={capacityBytes}
                stroke="#9ca3af"
                strokeDasharray="4 4"
                label={{
                  value: 'Capacity',
                  position: 'insideTopRight',
                  fill: '#6b7280',
                  fontSize: 10,
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke="#111827"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

const CompanyHistoryModal: React.FC<CompanyHistoryModalProps> = ({
  company,
  isOpen,
  onClose,
  pagePeriod,
}) => {
  const chartWindow = useMemo(
    () => (isOpen ? resolveChartWindow(pagePeriod) : null),
    [isOpen, pagePeriod],
  );

  const [trigger, { data, isFetching, isError, error }] =
    companiesApi.useLazyGetCompanyStatsTimeseriesQuery();

  useEffect(() => {
    if (!isOpen || !company || !chartWindow) return;
    const promise = trigger({
      companyId: company.companyId,
      fromDate: chartWindow.from,
      toDate: chartWindow.to,
    });
    return () => {
      promise.abort();
    };
  }, [isOpen, company, chartWindow, trigger]);

  const onRetry = () => {
    if (!company || !chartWindow) return;
    trigger({
      companyId: company.companyId,
      fromDate: chartWindow.from,
      toDate: chartWindow.to,
    });
  };

  const errorMessage = useMemo(() => {
    if (!isError) return null;
    const e = error as unknown as { status?: number; data?: unknown };
    if (e && typeof e.status === 'number' && e.status === 400) {
      return 'Date range rejected (too large or invalid). Try a shorter range.';
    }
    return 'Failed to load history.';
  }, [isError, error]);

  const capacityBytes = data
    ? data.currentValues.storageCapacityMegabytes * 1000 * 1000
    : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx="1rem">
        <ModalHeader pr={12}>
          <VStack align="start" spacing={1}>
            <Text
              fontSize="xs"
              letterSpacing="0.18em"
              textTransform="uppercase"
              color="gray.400"
              fontWeight={500}
            >
              History
            </Text>
            <Text fontSize="2xl" fontWeight={500} color="gray.900">
              {company?.companyName ?? ''}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={8}>
          {chartWindow?.defaultedFromLifetime && (
            <Text fontSize="xs" color="gray.500" mb={3}>
              Chart needs a date range — defaulting to last 12 months.
            </Text>
          )}
          {data && (
            <Text fontSize="xs" color="gray.400" mb={4} sx={TABULAR}>
              {data.effectiveFromDate.slice(0, 10)} → {data.effectiveToDate.slice(0, 10)}
              {' · '}
              {data.granularity} granularity
              {' · '}
              {data.buckets.length} bucket{data.buckets.length === 1 ? '' : 's'}
            </Text>
          )}
          {isFetching && !data && (
            <HStack justify="center" py={20}>
              <Spinner size="md" thickness="2px" speed="0.7s" color="gray.400" />
            </HStack>
          )}
          {!isFetching && isError && (
            <VStack py={16} spacing={3}>
              <Text color="gray.900" fontSize="md" fontWeight={500}>
                Couldn't load history.
              </Text>
              <Text color="gray.500" fontSize="sm">
                {errorMessage}
              </Text>
              <Box
                as="button"
                onClick={onRetry}
                fontSize="sm"
                color="gray.700"
                borderBottom="1px solid"
                borderColor="gray.400"
                _hover={{ color: 'gray.900', borderColor: 'gray.700' }}
              >
                Retry
              </Box>
            </VStack>
          )}
          {data && (
            <>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {SERIES_METRICS.map((m) => (
                  <ChartPanel
                    key={m.key}
                    metric={m}
                    values={data.series[m.key]}
                    buckets={data.buckets}
                    granularity={data.granularity}
                    capacityBytes={capacityBytes}
                  />
                ))}
              </SimpleGrid>
              <Box mt={8} pt={5} borderTop="1px solid" borderColor="gray.200">
                <Text
                  fontSize="xs"
                  letterSpacing="0.18em"
                  textTransform="uppercase"
                  color="gray.400"
                  fontWeight={500}
                  mb={2}
                >
                  Current snapshot
                </Text>
                <Text fontSize="sm" color="gray.700" sx={TABULAR}>
                  Admin users: {formatNumber(data.currentValues.adminUsers)}
                  {' · '}
                  Employee users: {formatNumber(data.currentValues.employeeUsers)}
                  {' · '}
                  Capacity: {formatBytes(capacityBytes)}
                </Text>
                <Text fontSize="xs" color="gray.400" mt={2}>
                  These metrics don&apos;t have history — only today&apos;s snapshot.
                </Text>
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CompanyHistoryModal;
